import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { SeismographService } from '../../services/seismograph-service';
import { Seismograph } from '../../../../shared/models/seismograph';
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { NewDeviceModalComponent } from '../add-device-modal/add-device-modal.component';
import { FilterModalComponent } from '../filter-modal/filter-modal.component';
import { Columns } from '../../models/Columns';
import { SeismographDevice } from '../../models/seismograph-device';
import { Router } from '@angular/router';
import { Site } from '../../../../shared/models/site';
import { LocalStorageService } from '@shared/services/utilities/local-storage.service';
import { VibrationsRecordService } from '@shared/services/records/vibrations-record.service';
import { AssignRecordsModalComponent } from '@shared/components/assign-records-modal/assign-records-modal.component';
import { EventService } from '@features/vibration-events/services/event-service';
import { EventData } from '@features/vibration-events/models/event-data';
import { VibrationsChart } from '@shared/models/charts/VibrationsChart';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ReportControlPointSelectionModalComponent, ReportGenerationParams } from '@features/report/report-control-point-selection-modal/report-control-point-selection-modal.component';
import { ControlPointInfoDto, ReportService } from '@shared/services/report/report.service';
import { CurrentStateService } from 'app/core/services/current-state.service';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { ControlPoint } from '@shared/models/controlPoint';

@Component({
    selector: 'app-seismograph-devices',
    standalone: true,
    imports: [TranslateModule, FormsModule, CommonModule, MatDialogModule, AngularSvgIconModule],
    templateUrl: './seismograph-devices-modal.component.html',
    styleUrls: ['./seismograph-devices-modal.component.scss'],
    providers: [DatePipe]
})

export class SeismographDevicesComponent implements OnInit {
    devices: SeismographDevice[] = [];
    filteredDevices: SeismographDevice[] = [];
    currentSortColumn: string | null = null;
    currentSortDirection: 'asc' | 'desc' = 'asc';
    modalOpened = false;
    currentSite: Site | undefined;
    currentSiteId: number | undefined;
    events: EventData[] = [];
    sitesList: any[] = [];
    controlPoints: ControlPoint[] = [];
    public filters = {
        device: '',
        version: '',
        company: '',
        site: '',
        lastUsed: '',
        lastCalibration: ''
    };

    public advancedfilters: {
        device?: string;
        version?: string;
        companyName?: string;
        siteName?: string;
        modelName?: string;
        lastUsedFrom?: string;
        lastUsedTo?: string;
        lastCalibrationFrom?: string;
        lastCalibrationTo?: string;
    } =
        {
            device: undefined,
            version: undefined,
            companyName: undefined,
            siteName: undefined,
            modelName: undefined,
            lastUsedFrom: undefined,
            lastUsedTo: undefined,
            lastCalibrationFrom: undefined,
            lastCalibrationTo: undefined,
        };

    constructor(
        private seismographService: SeismographService,
        private dialog: MatDialog,
        private cdRef: ChangeDetectorRef,
        private router: Router,
        private localStorageService: LocalStorageService,
        private dialogRecorde: MatDialog,
        private eventService: EventService,
        private datePipe: DatePipe,
        private translate: TranslateService,
        private reportService: ReportService,
        private currentStateService: CurrentStateService,
        private controlPointsService: ControlPointsService
    ) { }

    async ngOnInit(): Promise<void> {
        this.sitesList = this.localStorageService.getItem('allSites');
        try {
            await this.refreshDevices();
            
            this.currentSiteId = this.localStorageService.getItem('currentSiteID');
            this.loadEvents();
            this.loadControlPoints();

        } catch (error) {
            console.error('Initialization error:', error);
        }
    }

    protected getSiteName(siteId: number): string {
        const site = this.sitesList?.find((s: Site) => s.siteId === siteId);
        return site ? site.nom : '';
    }
        protected getRegionName(siteId: number): string {
        const site = this.sitesList?.find((s: Site) => s.siteId === siteId);
        return site ? site.region.nom : '';
    }
    private async refreshDevices(): Promise<void> {
        try {
            this.devices = [];
            this.filteredDevices = [];
    
            const seismographs = await this.seismographService.getAllSeismographs();
            const seismographIds = seismographs.map((s: Seismograph) => s.id);
            const [companyInstruments, companies] = await Promise.all([
                this.seismographService.getCompanyInstrumentsBySeismographIds(seismographIds),
                this.seismographService.getAllCompanies()
            ]);
    
            const companyInstrumentMap = new Map<number, any>(
                companyInstruments.map((ci) => [ci.seismographId, ci])
            );
            const companyMap = new Map<number, string>(
                companies.map((c) => [c.id, c.name])
            );
            const validCompanyIds = new Set<number>(companies.map((c) => c.id));
            this.devices = seismographs
                .filter((s) => !s.isDeleted)
                .map((s) => {
                    const instrument = companyInstrumentMap.get(s.id);
                    const companyId = instrument?.companyId;
                    const company = instrument && validCompanyIds.has(companyId)
                        ? companyMap.get(companyId)
                        : null;

                    return {
                        id: s.id,
                        siteId: s.siteId,
                        serialNumber: s.serialNumber,
                        model: s.model,
                        company: company ?? '',
                        lastUsed: s.lastUsed,
                        lastCalibration: s.lastCalibration,
                        state: s.connected ? 'Connected' : 'Disconnected',
                        favorite: s.isFavorite,
                        site: { region: this.getRegionName(s.siteId), name: this.getSiteName(s.siteId) },
                        batteryLevel: s.batteryLevel,
                        lastStatus: s.lastStatusDate,
                        connected: s.connected,
                        minutesAgo: s.minutesAgo,
                        workingStatus: s.workingStatus,
                        timeZoneName: s.timeZoneName,
                        timeZoneDisplayName: s.timeZoneDisplayName,
                        timeZoneId: s.timeZoneId
                    };
                });
    
            this.filteredDevices = this.devices;
            
            this.applyFilters();
    
        } catch (error) {
            console.error('Error refreshing seismograph devices:', error);
        }
    }

    applyFilters(): void {
        const companyFilter = this.filters.company?.trim().toLowerCase();
        const siteFilter = this.filters.site?.trim().toLowerCase();
        const deviceFilter = this.filters.device?.trim().toLowerCase();
        const lastUsedFilter = this.filters.lastUsed?.trim().toLowerCase();
        const lastCalibrationFilter = this.filters.lastCalibration?.trim().toLowerCase();

        this.filteredDevices = this.devices.filter((device) => {
            const isDeviceMatch = !deviceFilter || device.serialNumber.toLowerCase().includes(deviceFilter);
            const isCompanyMatch = !companyFilter || (device.company?.toLowerCase() ?? '').includes(companyFilter);
            const isSiteMatch = !siteFilter || (`${device.site?.region} - ${device.site?.name}`.toLowerCase()).includes(siteFilter);
            const isLastUsedMatch = !lastUsedFilter || new Date(device.lastUsed).toLocaleDateString().toLowerCase().includes(lastUsedFilter);
            const isLastCalibrationMatch = !lastCalibrationFilter || new Date(device.lastCalibration).toLocaleDateString().toLowerCase().includes(lastCalibrationFilter);

            return isDeviceMatch && isCompanyMatch && isSiteMatch && isLastUsedMatch && isLastCalibrationMatch;
        });
    }

    applyAdvancedFilters(): void {
        const trimAndLowerCase = (value?: string) => value?.trim().toLowerCase() || undefined;

        const companyFilter = trimAndLowerCase(this.advancedfilters.companyName);
        const siteFilter = trimAndLowerCase(this.advancedfilters.siteName);
        const deviceFilter = trimAndLowerCase(this.advancedfilters.device);
        const modelFilter = trimAndLowerCase(this.advancedfilters.modelName);

        const lastUsedFilterFrom = this.advancedfilters.lastUsedFrom ? new Date(this.advancedfilters.lastUsedFrom) : null;
        const lastUsedFilterTo = this.advancedfilters.lastUsedTo ? new Date(this.advancedfilters.lastUsedTo) : null;
        const lastCalibrationFilterFrom = this.advancedfilters.lastCalibrationFrom ? new Date(this.advancedfilters.lastCalibrationFrom) : null;
        const lastCalibrationFilterTo = this.advancedfilters.lastCalibrationTo ? new Date(this.advancedfilters.lastCalibrationTo) : null;

        const noFiltersApplied =
            !companyFilter &&
            !siteFilter &&
            !deviceFilter &&
            !modelFilter &&
            !lastUsedFilterFrom &&
            !lastUsedFilterTo &&
            !lastCalibrationFilterFrom &&
            !lastCalibrationFilterTo;

        if (noFiltersApplied) {
            this.filteredDevices = [...this.devices];
            return;
        }

        this.filteredDevices = this.devices.filter((device) => {
            const isDeviceMatch = deviceFilter ? device.serialNumber.toLowerCase().includes(deviceFilter) : true;
            const isCompanyMatch = companyFilter ? (device.company?.toLowerCase() || '').includes(companyFilter) : true;
            const isSiteMatch = siteFilter ? (`${device.site?.region} - ${device.site?.name}`.toLowerCase()).includes(siteFilter) : true;
            const isModelMatch = modelFilter ? (device.model?.toLowerCase() || '').includes(modelFilter) : true;

            const isLastUsedMatch = device.lastUsed
                ? (!lastUsedFilterFrom || new Date(device.lastUsed) >= lastUsedFilterFrom) &&
                (!lastUsedFilterTo || new Date(device.lastUsed) <= lastUsedFilterTo)
                : true;

            const isLastCalibrationMatch = device.lastCalibration
                ? (!lastCalibrationFilterFrom || new Date(device.lastCalibration) >= lastCalibrationFilterFrom) &&
                (!lastCalibrationFilterTo || new Date(device.lastCalibration) <= lastCalibrationFilterTo)
                : true;

            return (
                isDeviceMatch &&
                isCompanyMatch &&
                isSiteMatch &&
                isModelMatch &&
                isLastUsedMatch &&
                isLastCalibrationMatch
            );
        });
    }

    viewDeviceDetails(deviceSerialNumber: string): void {
        if (this.currentSiteId) {
            this.router.navigate(['/vibration-detail'], {
                queryParams: { deviceId: deviceSerialNumber, siteId: this.currentSiteId }
            });
        }
    }

    toggleSort(column: string): void {
        if (this.currentSortColumn === column) {
            this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.currentSortColumn = column;
            this.currentSortDirection = 'asc';
        }
        this.sortDevices();
    }

    private sortDevices(): void {
        if (!this.currentSortColumn) {
            return;
        }
        const directionMultiplier = this.currentSortDirection === 'asc' ? 1 : -1;

        this.filteredDevices.sort((a, b) => {
            const aValue = this.extractValue(a, this.currentSortColumn!);
            const bValue = this.extractValue(b, this.currentSortColumn!);

            if (aValue > bValue) {
                return directionMultiplier;
            } else if (aValue < bValue) {
                return -directionMultiplier;
            } else {
                return 0;
            }
        });
    }

    private extractValue(device: SeismographDevice, column: string): any {
        switch (column) {
            case Columns.SerialNumber:
                return device.serialNumber.toLowerCase();
            case Columns.Model:
                return device.model.toLowerCase();
            case Columns.Company:
                return device.company?.toLowerCase() || '';
            case Columns.Site:
                return `${device.site.region} - ${device.site.name}`.toLowerCase();
            case Columns.LastUsed:
                return new Date(device.lastUsed).getTime();
            case Columns.LastCalibration:
                return new Date(device.lastCalibration).getTime();
            case Columns.State:
                return device.state.toLowerCase();
            default:
                return '';
        }
    }

    async toggleFavorite(device: SeismographDevice): Promise<void> {
        device.favorite = !device.favorite;
        var seismograph = new Seismograph();

        seismograph.serialNumber = device.serialNumber;
        seismograph.isFavorite = device.favorite;

        await this.seismographService.updateSeismograph(seismograph);
    }

    onNewDeviceClick(): void {
        this.dialog.open(NewDeviceModalComponent, {
            data: {},
            enterAnimationDuration: '0ms'
        });

        this.modalOpened = true;
        this.dialog.afterAllClosed.subscribe(async () => {
            this.modalOpened = false;
            await this.refreshDevices();
            this.cdRef.detectChanges();
        });
    }

    trackByDevice(index: number, device: SeismographDevice): string {
        return device.serialNumber;
    }

    onFilterOpen(): void {
        const dialogRef = this.dialog.open(FilterModalComponent, {
            data: { initialFilters: { ...this.advancedfilters } },
            enterAnimationDuration: '0ms',
        });

        dialogRef.componentInstance.filtersChanged.subscribe((filters: any) => {
            if (Object.keys(filters).length === 0) {
                this.advancedfilters = {
                    device: undefined,
                    version: undefined,
                    companyName: undefined,
                    siteName: undefined,
                    modelName: undefined,
                    lastUsedFrom: undefined,
                    lastUsedTo: undefined,
                    lastCalibrationFrom: undefined,
                    lastCalibrationTo: undefined,
                };
            } else {
                this.advancedfilters = { ...this.advancedfilters, ...filters };
            }
            this.applyAdvancedFilters();
        });

        this.modalOpened = true;
        this.dialog.afterAllClosed.subscribe(() => {
            this.modalOpened = false;
            this.cdRef.detectChanges();
        });
    }
    loadEvents(): void {
        this.eventService.getVibrationEvents(this.currentSiteId, null).subscribe((response) => {
          this.events = response.data;
        });
      }

            loadControlPoints(): void {
                if (!this.currentSiteId) {
                    this.controlPoints = [];
                    return;
                }
                this.controlPointsService.getControlPointsBySiteId(this.currentSiteId, false).subscribe({
                    next: (response) => {
                        this.controlPoints = response;
                    },
                    error: (err) => {
                        console.error('Error loading control points', err);
                    }
                });
            }

            openReportModalForDevices(devices: SeismographDevice[]): void {
                const selectedSiteId = this.currentSiteId;
                const selectedDevices = devices.map(d => {
                    const s = new Seismograph();
                    s.id = d.id;
                    s.serialNumber = d.serialNumber;
                    s.siteId = d.siteId;
                    return s;
                });

                const dialogRef = this.dialog.open(ReportControlPointSelectionModalComponent, {
                    width: '450px',
                    disableClose: true,
                    autoFocus: false,
                    data: {
                        siteId: selectedSiteId,
                        controlPoints: this.controlPoints || [],
                        devices: selectedDevices,
                        lockDevicesSelection: false,
                        eventId: null,
                        eventName: undefined,
                        eventType: undefined,
                        location: undefined,
                        organisation: undefined
                    }
                });

                dialogRef.afterClosed().subscribe((params: ReportGenerationParams) => {
                    if (params) {
                        this.generateMultiRecordReport(params);
                    }
                });
            }

            private generateMultiRecordReport(params: ReportGenerationParams): void {
                const controlPointsDto: ControlPointInfoDto[] = params.controlPoints.map(cp => ({
                    ControlPointId: cp.ControlPointId,
                    ControlPointName: cp.ControlPointName
                }));

                this.currentStateService.setReportGenerationInProgress(true);
                this.reportService.generateVibrationReport(
                    undefined,
                    params.startDate,
                    params.endDate,
                    params.ponderationId,
                    params.eventName,
                    params.eventType,
                    this.sitesList?.find((s: Site) => s.siteId === this.currentSiteId)?.nom,
                    params.organisation,
                    controlPointsDto,
                    this.currentSiteId,null,
                    params.devices || null

                ).subscribe({
                    next: (blob) => {
                        this.downloadPDF(blob, `Report_Devices_${new Date().toLocaleDateString('fr-FR')}.pdf`);
                        this.currentStateService.setReportGenerationInProgress(false);
                    },
                    error: async (error) => {
                        console.error('Error generating multi-record report:', error);
                        const errorMessage = await this.extractErrorMessage(error);
                        if (errorMessage) {
                            this.translate.get('ERRORS.ERROR').subscribe((fallback) => {
                                console.error(fallback, errorMessage);
                            });
                        }
                        this.currentStateService.setReportGenerationInProgress(false);
                    }
                });
            }

            private downloadPDF(blob: Blob, filename: string): void {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }

            private async extractErrorMessage(error: any): Promise<string | null> {
                if (error?.error instanceof Blob) {
                    try {
                        const text = await error.error.text();
                        const jsonError = JSON.parse(text);
                        return jsonError.message || null;
                    } catch (e) {
                        return null;
                    }
                }
                return error?.error?.message || error?.message || null;
            }

      assignMultiRecords(): void {
         const dialogConfig = new MatDialogConfig();
              dialogConfig.autoFocus = false;
              dialogConfig.data = {
                siteId: this.currentSiteId,
                eventId: null,
                controlPointId: null,
                events: this.events,
              };
              dialogConfig.enterAnimationDuration = '0ms';
      
              this.dialogRecorde.open(AssignRecordsModalComponent, dialogConfig);
      }
      filterRecords(records: VibrationsChart[]): VibrationsChart[] {
        return records.filter(record => record.controlPointId == null || record.eventId == null)
      }
    getBatteryIcon(device: SeismographDevice): 'charging' | 'low' | 'medium' | 'battery' | 'full-battery' | 'unknown' {
        const level = device.batteryLevel;
        if (level == null) return 'unknown';
        const isConnected = this.getDeviceStatusInfo(device).isConnected;
        if (isConnected && level > 100) return 'charging';
        if (level <= 20) return 'low';
        if (level <= 50) return 'medium';
        if (level <= 80) return 'battery';
        return 'full-battery';
    }
        protected getDeviceStatusInfo(device: SeismographDevice): { isConnected: boolean, lastActive: string } {
            return {
                isConnected: !!device?.connected,
                lastActive: device.minutesAgo != null && device?.connected ? this.formatMinutesAgo(device.minutesAgo) : '--'
            };
        }

        private getStatusDateLocalLabel(device: SeismographDevice): string {
            const rawTimestamp = device?.lastStatus ? String(device.lastStatus).replace('T', ' ') : '--';
            console.log(rawTimestamp);
            
            return rawTimestamp == '--' ? rawTimestamp : `${rawTimestamp} (local)`;
        }

        getStatusIcon(device: SeismographDevice): string {
            const { isConnected } = this.getDeviceStatusInfo(device);
            return isConnected ? './icon/active.svg' : './icon/red.svg';
        }

        getStatusTitle(device: SeismographDevice): string {
            const { isConnected, lastActive } = this.getDeviceStatusInfo(device);
            return this.translate.instant('DEVICE.STATUS_INFO', {
                status: isConnected ? this.translate.instant('DEVICE.ACTIVE') : this.translate.instant('DEVICE.STATUS'),
                date: isConnected ? lastActive : '--'
            }) + (device.workingStatus ? ` - ${device.workingStatus}` : '');
        }

        private formatMinutesAgo(minutesAgo: number): string {
            if (minutesAgo < 60) {
                return this.translate.instant('DEVICE_STATUS.MINUTES_AGO', { minutes: minutesAgo });
            }

            const hours = Math.floor(minutesAgo / 60);
            const minutes = minutesAgo % 60;

            return minutes > 0
                ? this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_MINUTES_AGO' : 'DEVICE_STATUS.HOURS_MINUTES_AGO', { hours, minutes })
                : this.translate.instant(hours === 1 ? 'DEVICE_STATUS.HOUR_AGO' : 'DEVICE_STATUS.HOURS_AGO', { hours });
        }
}